import React from 'react';
import { Link, Box, Card, CardContent, Typography } from '@mui/material';
import moment from 'moment';
import { upperFirst } from 'lodash';
import Image from 'next/image';
import { buildImageLoaderUrl } from '../../utils/utils';

const styles = {
  card: {
    width: 300,
    margin: 'auto',
    textAlign: 'left' as const,
  },
  date: {
    fontSize: '10px !important',
    marginBottom: '10px !important',
  },
  contentContainer: {
    padding: '0 !important',
  },
  content: {
    padding: '5px 5px 5px 0',
  },
  title: {
    '&:hover': {
      opacity: 0.5,
    },
  },
  imgContainer: {
    overflow: 'hidden',
    img: {
      transform: 'scale(1)',
      transition: 'transform 300ms ease',
      overflow: 'hidden',
      cursor: 'pointer',
    },
    'img:hover': {
      transform: 'scale(1.2)',
      transition: 'transform 300ms ease',
    },
  },
  imgWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative' as const,
    minHeight: '300px',
  },
};

interface Picture {
  main?: boolean;
  originalPicturePath?: string;
}

interface Article {
  url: string;
  label: string;
  createdAt: string | number;
  shortDescription?: string;
  authorName?: string;
  pictures: Picture[];
}

interface ArticleCardProps {
  article: Article;
}

export default function ArticleCard({ article }: ArticleCardProps) {
  const picture = Array.isArray(article?.pictures) ? article.pictures.slice(0) : [];

  const image = picture.sort((a, b) => (a.main ? -1 : 1))[0]?.originalPicturePath || '/icons/planet.svg';
  moment.locale('fr');
  const date = moment(parseInt(String(article.createdAt))).format('dddd DD MMMM YYYY');

  const myLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
    return src.startsWith('/static') ? buildImageLoaderUrl(src, width, quality) : src;
  };

  const link = `/article/${article.url}`;

  return (
    <Card sx={styles.card} square={true} elevation={0}>
      <CardContent sx={styles.contentContainer}>
        <Link href={link}>
          <Box sx={styles.imgContainer}>
            {image && (
              <div style={styles.imgWrapper}>
                <Image
                  loader={myLoader}
                  fill
                  style={{ objectFit: 'contain' }}
                  src={image}
                  alt={article.label}
                  priority
                />
              </div>
            )}
          </Box>
        </Link>
        <Box sx={styles.content}>
          <Typography sx={styles.date}>{upperFirst(date)}</Typography>
          {article?.authorName && (
            <Typography variant="caption" color="text.secondary">
              Auteur: {article.authorName}
            </Typography>
          )}
          <Link href={link} underline="none">
            <Typography variant="h5" component="div" sx={styles.title}>
              {article.label}
            </Typography>
          </Link>
          <Typography variant="body2" color="text.secondary">
            {article.shortDescription}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
